/**
 * motivationalSayings.ts
 * ──────────────────────────────────────────────────────────────
 * Companion-identity gamification PR (2026-05-03).
 *
 * Short, voice-guide-compliant aphorisms that appear as transient
 * banners on subject intros, practice entry, and exam entry. Their
 * job: compete with TikTok / WhatsApp / Facebook for the student's
 * attention by reframing the moment ("studying matters") without
 * preaching.
 *
 * Editorial constraints:
 *   - Under ~80 chars after subject interpolation
 *   - Forward-pointing, never lecturing
 *   - PT-MZ register (no PT-PT/PT-BR slang from the banned list)
 *   - Subject-aware OR subject-agnostic — pick whichever lands
 *
 * Used by <MotivationalBanner /> which auto-dismisses after a few
 * seconds. Banner is non-blocking; the student's primary action
 * (Vamos lá / answer the question / start the exam) remains
 * interactive throughout.
 *
 * To extend: append below. Keep aphoristic; no specific-claim
 * promises ("vais passar com 18"), no platitudes that read as
 * generic ("o estudo é importante").
 */

/**
 * Exam intro framing — companion-voice copy that gives the student a
 * moment to mentally prepare before the timer starts. Per the user
 * direction: "Exames são bons porque dão a mesma seriedade como se
 * estivesse numa sala de aula com papel e caneta, vamos a isso."
 *
 * Two banks: 'mini' for Mini-Exames (shorter, lower stakes) and
 * 'full' for full Exame de Simulação (longer, full-paper format).
 * Picked at random per ExamPage mount.
 */
export function pickExamIntro(kind: 'mini' | 'full'): string {
  if (kind === 'mini') {
    const variants = [
      'Mini Exame: simulação rápida com a mesma seriedade da sala de aula. Vamos a isso.',
      'Tempo a contar, como num teste a sério. Vamos lá.',
      'Mini Exame: hora de medir o que sabes em condições reais. Vamos a isso.',
    ]
    return variants[Math.floor(Math.random() * variants.length)]
  }
  const variants = [
    'Exames são bons porque dão a mesma seriedade como se estivesses numa sala de aula com papel e caneta. Vamos a isso.',
    'Exame de Simulação: tempo a contar, como na sala. Vamos a isso.',
    'Hora do exame. Mesma seriedade da sala de aula — vamos lá mostrar o que sabes.',
  ]
  return variants[Math.floor(Math.random() * variants.length)]
}

/**
 * Practice-entry intro: a subject-specific opener shown when the
 * student lands on /questions?subject=X. Same banner pattern as the
 * motivational sayings (auto-dismissing), but the message is
 * specifically about THIS subject.
 *
 * Per the user direction: warm + voice-led + 3 variants per subject
 * so it feels alive without being random. Picked at random per page
 * mount. Subject name is the parameter (e.g., "Matemática").
 */
export function pickPracticeIntro(subjectName: string): string {
  const variants = [
    `Vamos calibrar os teus conhecimentos de ${subjectName}.`,
    `${subjectName} agora. Vamos a isso.`,
    `Aquece com ${subjectName}. Estamos juntos nisto.`,
  ]
  return variants[Math.floor(Math.random() * variants.length)]
}

/**
 * Exam-nudge sayings — occasionally surfaced on /subjects to invite
 * a student who has practiced enough to take a Mini-Exame or
 * Exame de Simulação.
 *
 * Per the user direction: "sometimes convince them to try mini exams
 * and full exams." Picked at random when the caller has decided this
 * is a good moment (typically: student has done ≥10 attempts, no
 * exam taken in the last week). The decision logic lives in the
 * caller — this function just hands back the copy.
 */
export function pickExamNudge(): string {
  const variants = [
    'Já dominas a teoria? Mete um Mini Exame para medires onde estás.',
    'Tens base suficiente. Hora de testar com um Exame de Simulação.',
    'Mini Exame em poucos minutos. Vê o teu nível na prática.',
    'Vamos lá medir-te a sério. Um Mini Exame mostra a tua posição real.',
  ]
  return variants[Math.floor(Math.random() * variants.length)]
}

export function pickMotivationalSaying(subjectName: string): string {
  const sayings = [
    'A dedicação de hoje será importante num futuro próximo.',
    'A educação é a chave do sucesso.',
    'O conhecimento move montanhas. Vamos a isso.',
    'Quem estuda hoje, escolhe melhor amanhã.',
    `Cada questão de ${subjectName} é um passo à frente.`,
    'Disciplina pequena, vitória grande.',
    'Pequenos passos, grandes destinos.',
    'Hoje treinas, amanhã decides.',
    'O esforço de agora vale por dois depois.',
    'Quem se prepara, abre portas.',
    'Não é talento, é constância.',
    'Cada minuto aqui é um minuto contra o esquecimento.',
    'A sorte é a constância disfarçada.',
    'Quem conhece, escolhe melhor.',
    'Estudar agora é desenhar o teu próprio futuro.',
  ]
  return sayings[Math.floor(Math.random() * sayings.length)]
}
